import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {UserIsAdminGuard} from "./guards/user-is-admin-guard";
import {LogoutPage} from "./pages/auth/logout/logout.page";
import {UserIsNotAdminPageComponent} from "./pages/user-is-not-admin-page/user-is-not-admin-page.component";
import {
  ManageGamePageComponent
} from "./pages/manage-game/manage-game-page.component";
import {PlayerSearchComponent} from "./pages/player-search/game/player-search.component";
import {GameSelectedGuard} from "./guards/game-selected-guard";
import {
  ManageUsersComponent
} from "./pages/manage-users/manage-users.component";
import {RecoverPasswordComponent} from "./pages/recover-password/recover-password.component";
import {UpdatePasswordComponent} from "./pages/update-password/update-password.component";
import {UpdateEmailAddressComponent} from "./pages/update-email-address/update-email-address.component";
import {AuditingComponent} from "./pages/auditing/auditing.component";
import {ErrorDebuggingComponent} from "./pages/error-debugging/error-debugging.component";
import {PermissionGuard} from "./guards/permission-guard";
import {PermissionTypes} from "./enum/PermissionTypes";
import {NotAuthorizedComponent} from "./pages/not-authorized/not-authorized.component";
import {TestingPageComponent} from "./pages/testing-page/testing-page.component";
import {NewGameComponent} from "./pages/new-game/new-game.component";
import {GameRulesEditorComponent} from "./pages/game-rules-editor/game-rules-editor.component";
import {HowToPlayEditorComponent} from "./pages/how-to-play-editor/how-to-play-editor.component";
import {ActiveGameInstancesComponent} from "./pages/active-game-instances/active-game-instances.component";
import {LoginGuard} from "./guards/login.guard";
import {AdminAuthCallbackPage} from "./pages/auth/admin-auth-callback/admin-auth-callback.page";
import {LoginComponent} from "./pages/auth/login/login.component";
import {CausableAdminGuard} from "./guards/causable-admin-guard";
import {SentEmailsComponent} from "./pages/sent-emails/sent-emails.component";
import {
  FrequentlyAskedQuestionsComponent
} from "./pages/frequently-asked-questions/frequently-asked-questions.component";
import {LandingComponent} from "./pages/landing/landing.component";
import {CharityAdminGuard} from "./guards/charity-admin.guard";
import {SupportDeskComponent} from "./pages/support-desk/support-desk.component";
import {ManagePlayerComponent} from "./pages/manage-player/manage-player.component";
import {CheckForSingleGameAccessResolver} from "./resolvers/check-for-single-game-access.resolver";
import {ProfanityFilterManagerComponent} from "./pages/profanity-filter-manager/profanity-filter-manager.component";
import {ErrorCategoryManagementComponent} from "./pages/error-category-management/error-category-management.component";
import {
  GameInstancePlayerEmailsComponent
} from "./pages/game-instance-player-emails/game-instance-player-emails.component";
import {GlobalPlayerSearchComponent} from "./pages/player-search/global/global-player-search.component";
import {
  ManageGlobalPermissionsComponent
} from "./pages/manage-roles-permission/manage-global-permissions/manage-global-permissions.component";
import {
  CrossGameActiveDrawLandingComponent
} from "./pages/cross-game-active-draw-landing/cross-game-active-draw-landing.component";
import {
  NotificationPreferencesComponent
} from "./pages/notification-preferences/notification-preferences.component";
import {
  CharityManagementPageComponent
} from "./pages/manage-charities/charity-management-page.component";
import {CharityComponent} from "./pages/charity/charity.component";
import {ReportingPageComponent} from "./pages/reports/reporting-page.component";
import {ReportShellComponent} from "./pages/reports/report-shell/report-shell.component";
import {LandingPageRedirectGuard} from "./guards/landing-page-redirect.guard";
import {AddNewRoleComponent} from "./pages/manage-roles-permission/add-new-role/add-new-role.component";
import {
  DrawWinnerLandingComponent
} from "./pages/draw-winner/draw-winner-landing.component";
import {
  WinnerPayoutsPageComponent
} from "./pages/winner-payouts/winner-payouts-page.component";
import {
  CharityPayoutsPageComponent
} from "./pages/charity-payouts/charity-payouts-page.component";
import {
  PayoutRulesPageComponent
} from "./pages/payout-rules/payout-rules-page.component";
import {
  CausableLedgerPageComponent
} from "./pages/causable-ledger/causable-ledger-page.component";
import {ViewEditRoleComponent} from "./pages/manage-roles-permission/view-edit-role/view-edit-role.component";
import {SignalRConnectionsComponent} from "./pages/signal-r-connections/signal-r-connections.component";
import {LogLevelSwitchComponent} from "./pages/log-level-switch/log-level-switch.component";
import {
  PlayerEmailConfirmationsComponent
} from "./components/player-email-confirmations/player-email-confirmations.component";
import {AdminTermsComponent} from "./pages/admin-terms/admin-terms.component";
import {
  CreateBroadcastNotificationComponent
} from "./pages/create-broadcast-notification/create-broadcast-notification.component";
import {ManageNotificationsComponent} from "./pages/manage-broadcast-notifications/manage-notifications.component";
import {PlayerEmailHistoryComponent} from "./pages/player-email-history/player-email-history.component";
import {FileIntegrityDashboardComponent} from "./pages/file-integrity-dashboard/file-integrity-dashboard.component";
import {ManageRegionTaxRatesComponent} from "./pages/manage-region-tax-rates/manage-region-tax-rates.component";
import {
  PaymentMethodsComponent
} from "./pages/payment-methods/payment-methods.component";
import {
  DuplicateCardFingerprintsComponent
} from "./pages/duplicate-card-fingerprints/duplicate-card-fingerprints.component";
import {FailedRefundsComponent} from "./pages/failed-refunds/failed-refunds.component";
import {UserDetailsComponent, UserDetailsRouteData} from "./pages/user-details/user-details.component";

const routes: Routes = [
  {
    path: '',
    title: 'Landing',
    canActivate: [LandingPageRedirectGuard],
    resolve: [CheckForSingleGameAccessResolver],
    component: LandingComponent
  },
  {
    path: 'not-admin',
    title: 'Not Admin',
    component: UserIsNotAdminPageComponent
  },
  {
    path: 'not-authorized',
    title: 'Not Authorized',
    component: NotAuthorizedComponent
  },
  {
    path: 'forgot-my-password',
    title: 'Recover Password',
    component: RecoverPasswordComponent
  },
  {
    path: 'auth/admin-auth-callback',
    canActivate: [LoginGuard],
    component: AdminAuthCallbackPage
  },
  {
    path: 'auth/login',
    canActivate: [LoginGuard],
    component: LoginComponent
  },
  {
    path: 'logout',
    title: 'Logout',
    canActivate: [UserIsAdminGuard],
    component: LogoutPage
  },
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full'
  },

  // admin required routes
  {
    path: '',
    canActivate: [UserIsAdminGuard],
    children: [
      {
        path: 'privacy-policy',
        title: 'Admin Terms of Service',
        component: AdminTermsComponent
      },
      {
        path: 'frequently-asked-questions',
        title: 'Frequently Asked Questions',
        component: FrequentlyAskedQuestionsComponent
      },
      {
        path: 'cross-game-charities',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewCharities)],
        title: 'Charity Management',
        component: CharityManagementPageComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'view-player-emails',
        title: 'Game Instance Player Emails',
        canActivate: [CausableAdminGuard],
        component: GameInstancePlayerEmailsComponent
      },
      {
        path: 'sent-emails',
        title: 'Sent Emails',
        canActivate: [CausableAdminGuard],
        component: SentEmailsComponent
      },
      {
        path: 'signal-r-connections',
        title: 'Signal R Connections',
        canActivate: [CausableAdminGuard],
        component: SignalRConnectionsComponent
      },
      {
        path: 'game-instance-game-emails',
        title: 'Game Instance Player Emails',
        canActivate: [CausableAdminGuard],
        component: GameInstancePlayerEmailsComponent
      },
      {
        path: 'new-game',
        title: 'New Game',
        canActivate: [CausableAdminGuard],
        component: NewGameComponent,
        children: [
          {
            path: ':/newGameId',
            component: NewGameComponent
          },
        ]
      },
      {
        path: 'profanity-filter-manager',
        title: 'Profanity Filter Manager',
        canActivate: [CausableAdminGuard],
        component: ProfanityFilterManagerComponent
      },
      {
        path: 'error-category-management',
        title: 'Error Category Management',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewSystemErrorMessages)],
        component: ErrorCategoryManagementComponent
      },

      {
        path: 'testing-page',
        title: 'Testing Page',
        component: TestingPageComponent,
        canActivate: [CausableAdminGuard]
      },
      {
        path: 'cross-game-game-management',
        title: 'Manage Game',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGameSettings)],
        component: ManageGamePageComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'auditing',
        title: 'Auditing',
        canActivate: [CausableAdminGuard],
        component: AuditingComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'error-debugging',
        title: 'Error Debugging',
        canActivate: [CausableAdminGuard],
        component: ErrorDebuggingComponent
      },
      {
        path: 'cross-game-support-desk-search',
        title: 'Support Desk',
        component: SupportDeskComponent,
        data: {isCrossGame: true},
        canActivate: [() => PermissionGuard(PermissionTypes.ViewSupportRequests)]
      },
      {
        path: 'cross-game-winner-payouts',
        title: 'Winner Payouts',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewWinnerPayouts)],
        component: WinnerPayoutsPageComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'cross-game-charity-payouts',
        title: 'Charity Payouts',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewCharityPayouts)],
        component: CharityPayoutsPageComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'update-password',
        title: 'Update Password',
        component: UpdatePasswordComponent
      },
      {
        path: 'update-email-address',
        title: 'Update Email Address',
        component: UpdateEmailAddressComponent
      },
      {
        path: 'charity/:charityId',
        title: 'Charity',
        canActivate: [CharityAdminGuard],
        component: CharityComponent
      },
      {
        path: 'cross-game-admin-reporting',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewReports)],
        title: 'Reports',
        component: ReportingPageComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'cross-game-report-builder',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewDynamicReport)],
        title: 'Report Builder',
        component: ReportShellComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'global-player-search',
        title: 'System Player Search',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGlobalPlayerSearch)],
        component: GlobalPlayerSearchComponent
      },
      {
        path: 'email-history',
        title: 'Email History',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: PlayerEmailHistoryComponent
      },
      {
        path: 'manage-cross-game-users',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewAdminUsers)],
        title: 'Manage Users',
        component: ManageUsersComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'cross-game-view-draw-landing',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGameSummaryReport)],
        title: 'Active Draws',
        component: CrossGameActiveDrawLandingComponent
      },
      {
        path: 'cross-game-notification-preferences',
        title: 'Notification Preferences',
        component: NotificationPreferencesComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'manage-player/:playerId',
        title: 'Manage Game Global Player',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGlobalPlayerSearch)],
        component: ManagePlayerComponent
      },
      {
        path: 'manage-global-permissions',
        canActivate: [CausableAdminGuard],
        title: 'Manage Global Permissions',
        component: ManageGlobalPermissionsComponent
      },
      {
        path: 'cross-game-draw-winner',
        title: 'Draw Winner',
        canActivate: [() => PermissionGuard(PermissionTypes.DrawGameWinner)],
        component: DrawWinnerLandingComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'add-new-role',
        canActivate: [CausableAdminGuard],
        title: 'Add New Role',
        component: AddNewRoleComponent
      },
      {
        path: 'view-edit-role/:roleId',
        title: 'View Edit Role',
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyRolesAndPermissions)],
        component: ViewEditRoleComponent
      },
      {
        path: 'log-level-switch',
        title: 'Log Level Switch',
        canActivate: [CausableAdminGuard],
        component: LogLevelSwitchComponent
      },
      {
        path: 'player-email-confirmations',
        title: 'Player Email Confirmations',
        component: PlayerEmailConfirmationsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewEmailConfirmations)]
      },
      {
        path: 'manage-broadcast-notifications',
        title: 'Manage Broadcast Notifications',
        component: ManageNotificationsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewBroadcastNotifications)]
      },
      {
        path: 'create-broadcast-notification',
        title: 'Create Broadcast Notification',
        component: CreateBroadcastNotificationComponent,
        data: {allowScopeSelection: true},
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyBroadcastNotifications)]
      },
      {
        path: 'edit-broadcast-notification/:id',
        title: 'Edit Broadcast Notification',
        component: CreateBroadcastNotificationComponent,
        data: {allowScopeSelection: true},
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyBroadcastNotifications)]
      },
      {
        path: 'file-integrity-dashboard',
        title: 'File Integrity Dashboard',
        component: FileIntegrityDashboardComponent,
        canActivate: [CausableAdminGuard]
      },
      {
        path: 'manage-region-tax-rates',
        title: 'Manage Region Tax Rates',
        component: ManageRegionTaxRatesComponent,
        canActivate: [CausableAdminGuard]
      },
      {
        // Causable's own receivable ledger — global scope only, Causable staff only.
        path: 'cross-game-causable-ledger',
        title: 'Causable Ledger',
        component: CausableLedgerPageComponent,
        canActivate: [CausableAdminGuard],
        data: {isCrossGame: true}
      },
      {
        path: 'payment-methods',
        title: 'Payment Methods',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: PaymentMethodsComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'duplicate-card-fingerprints',
        title: 'Duplicate Card Fingerprints',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewDuplicateCardFingerprints)],
        component: DuplicateCardFingerprintsComponent,
        data: {isCrossGame: true}
      },
      {
        path: 'failed-refunds',
        title: 'Failed Refunds',
        component: FailedRefundsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewFailedRefunds)]
      },
      {
        path: 'cross-game-user-details/:userId',
        component: UserDetailsComponent,
        data: {
          backUrl: 'manage-cross-game-users',
          useGameContext: false
        } as UserDetailsRouteData
      }
    ]
  },
  // game and admin required routes
  {
    path: ':gameId',
    canActivate: [UserIsAdminGuard, GameSelectedGuard],
    children: [
      {
        path: 'manage-game-admin-users',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewAdminUsers)],
        title: 'Manage Users',
        component: ManageUsersComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'game-admin-user-details/:userId',
        component: UserDetailsComponent,
        data: {
          backUrl: 'manage-game-admin-users',
          useGameContext: true
        } as UserDetailsRouteData
      },
      {
        path: 'game-admin-winner-payouts',
        title: 'Winner Payouts',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewWinnerPayouts)],
        component: WinnerPayoutsPageComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'game-admin-charity-payouts',
        title: 'Charity Payouts',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewCharityPayouts)],
        component: CharityPayoutsPageComponent,
        data: {isCrossGame: false}
      },
      {
        // Per-game config, so there is no cross-game twin: each organization's payout rules are its own.
        path: 'game-admin-payout-rules',
        title: 'Payout Rules',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGamePayoutRules)],
        component: PayoutRulesPageComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'game-admin-notification-preferences',
        title: 'Notification Preferences',
        component: NotificationPreferencesComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'charity/:charityId',
        title: 'Charity',
        canActivate: [CharityAdminGuard],
        component: CharityComponent
      },
      {
        path: 'game-admin-reporting',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewReports)],
        title: 'Reports',
        component: ReportingPageComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'report-builder',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewDynamicReport)],
        title: 'Report Builder',
        component: ReportShellComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'auditing',
        title: 'Auditing',
        canActivate: [CausableAdminGuard],
        component: AuditingComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'draw-winner',
        title: 'Draw Winner',
        canActivate: [() => PermissionGuard(PermissionTypes.DrawGameWinner)],
        component: DrawWinnerLandingComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'manage-game',
        title: 'Manage Game',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewGameSettings)],
        component: ManageGamePageComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'manage-player/:playerId',
        title: 'Manage Game Players',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: ManagePlayerComponent
      },
      {
        path: 'manage-charities',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewCharities)],
        title: 'Charity Management',
        component: CharityManagementPageComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'active-game-instances',
        title: 'Current Draw',
        component: ActiveGameInstancesComponent
      },
      {
        path: 'player-search',
        title: 'Player Search',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: PlayerSearchComponent
      },
      {
        path: 'payment-methods',
        title: 'Payment Methods',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: PaymentMethodsComponent,
        data: {isCrossGame: false}
      },
      {
        path: 'duplicate-card-fingerprints',
        title: 'Duplicate Card Fingerprints',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewDuplicateCardFingerprints)],
        component: DuplicateCardFingerprintsComponent,
        data: {isCrossGame: false}
      },
      // Guarded on what the save endpoints actually require (#1501). Both routes asked for
      // ModifyGameSettings while game/game-rules and game/how-to-play are gated on
      // EditGameTermsOfService / EditGameHowToPlay, so the guard and the API disagreed in both directions:
      // an admin holding only the edit permission was turned away from a screen whose Edit button they
      // could see, and an admin holding only ModifyGameSettings was let into an editor that 403ed on save.
      {
        path: 'edit-game-rules',
        title: 'Edit Game Rules',
        component: GameRulesEditorComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.EditGameTermsOfService)]
      },
      {
        path: 'edit-how-to-play',
        title: 'Edit How To Play',
        component: HowToPlayEditorComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.EditGameHowToPlay)]
      },
      {
        path: 'game-admin-support-desk-search',
        title: 'Support Desk',
        component: SupportDeskComponent,
        data: {isCrossGame: false},
        canActivate: [() => PermissionGuard(PermissionTypes.ViewSupportRequests)]
      },
      {
        path: 'manage-broadcast-notifications',
        title: 'Broadcast Notifications',
        component: ManageNotificationsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewBroadcastNotifications)]
      },
      {
        path: 'create-broadcast-notification',
        title: 'Create Broadcast Notification',
        component: CreateBroadcastNotificationComponent,
        data: {allowScopeSelection: false},
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyBroadcastNotifications)]
      },
      {
        path: 'edit-broadcast-notification/:id',
        title: 'Edit Broadcast Notification',
        component: CreateBroadcastNotificationComponent,
        data: {allowScopeSelection: false},
        canActivate: [() => PermissionGuard(PermissionTypes.ModifyBroadcastNotifications)]
      },
      {
        path: 'frequently-asked-questions',
        title: 'Frequently Asked Questions',
        component: FrequentlyAskedQuestionsComponent
      },
      // Game-scoped variants of the formerly global-only player screens, so navigating from a game keeps the game in
      // context.
      {
        path: 'email-history',
        title: 'Email History',
        canActivate: [() => PermissionGuard(PermissionTypes.ViewPlayerInfo)],
        component: PlayerEmailHistoryComponent
      },
      {
        path: 'player-email-confirmations',
        title: 'Player Email Confirmations',
        component: PlayerEmailConfirmationsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewEmailConfirmations)]
      },
      {
        path: 'failed-refunds',
        title: 'Failed Refunds',
        component: FailedRefundsComponent,
        canActivate: [() => PermissionGuard(PermissionTypes.ViewFailedRefunds)]
      },
    ]
  }
];

// initialNavigation: "disabled" stops navigation until we are ready (re-enabled in app component when org and auth config have been set up)
@NgModule({
  imports: [RouterModule.forRoot(routes, {initialNavigation: "disabled"})],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
